import React, { useRef, useState } from 'react'
import { Formik, Form } from 'formik'
import { PageHeader } from '../components/common'
import { Col, Container, Row, Spinner, Table } from 'react-bootstrap'
import { ClearFilters } from '../components/common'
import {
  Button,
  useNotificationService,
  useErrorService,
} from '@abroad/components'
import reportConstants from '../constants/reports'
import { CustomSelectionField } from '../components/form'
import API from '../utils/API'
import SessionDetailsTable from '../components/reports/SessionDetailsReportTable'

const Reports = () => {
  const [entityNames, setEntityNames] = useState([])
  const [entityNamesLoading, setEntityNamesLoading] = useState(false)
  const [reportData, setReportData] = useState(null)
  const [reportLoading, setReportLoading] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  // Prevent double-clicks from starting parallel generate + export jobs
  const requestInFlightRef = useRef(false)
  const Error = useErrorService()
  const Notification = useNotificationService()

  const isLoading = reportLoading || exportLoading

  const initialValues = {
    reportType: '',
    entityType: '',
    entityName: '',
    sessionFilters: [],
  }

  const sessionFilterOptions = [
    { value: 'Regular Sessions', label: 'Regular Sessions' },
    { value: 'Intentions & Insights', label: 'Intentions & Insights' },
    { value: 'Growth Sessions', label: 'Growth Sessions' },
    { value: 'Group Sessions', label: 'Group Sessions' },
  ]

  const handleUserClick = (userId) => {
    if (userId) {
      window.open(`/admin/users/${userId}`, '_blank')
    }
  }

  const isClickableColumn = (columnKey) => {
    const lowerKey = columnKey.toLowerCase()
    // entityName is saffron-styled but not a user link
    if (lowerKey === 'entityname') {
      return false
    }
    return lowerKey.includes('name') || lowerKey.includes('email')
  }

  const isSaffronColumn = (columnKey) => {
    const lowerKey = columnKey.toLowerCase()
    return lowerKey.includes('name') || lowerKey.includes('email')
  }

  const isAnyFilterActive = (values) => {
    return (
      values.reportType ||
      values.entityType ||
      values.entityName ||
      (Array.isArray(values.sessionFilters) && values.sessionFilters.length > 0)
    )
  }

  const handleClearFilters = (setFieldValue) => {
    setFieldValue('reportType', '')
    setFieldValue('entityType', '')
    setFieldValue('entityName', '')
    setFieldValue('sessionFilters', [])
    setReportData(null)
  }

  const fetchEntityNames = async (entityType) => {
    if (!entityType) {
      setEntityNames([])
      return
    }

    try {
      setEntityNamesLoading(true)

      const response = await API.AdminAPI.report.getEntityName(entityType)

      if (response && Array.isArray(response)) {
        setEntityNames(response)
      } else if (response?.data && Array.isArray(response.data)) {
        setEntityNames(response.data)
      } else if (response?.result && Array.isArray(response.result)) {
        setEntityNames(response.result)
      } else {
        setEntityNames([])
      }
    } catch (error) {
      Error.showError(error)
      setEntityNames([])
    } finally {
      setEntityNamesLoading(false)
    }
  }

  const handleEntityNameClick = (entityType, e) => {
    if (!entityType) {
      e.preventDefault()
      Notification.showNotification(
        'Please select an entity type first.',
        'danger',
      )
      return false
    }
  }

  // Report type required; entity type, entity name, and fields are optional for all reports.
  const validateReportFilters = (values) => {
    if (!values.reportType) {
      Notification.showNotification('Please select a report type.', 'danger')
      return false
    }

    return true
  }

  const buildReportRequestBody = (values) => {
    const requestBody = {
      fields: {},
    }

    if (values.entityType) {
      requestBody.entityType = values.entityType
    }

    const selectedEntity = entityNames.find(
      (entity) => entity.id === values.entityName,
    )

    if (selectedEntity) {
      requestBody.id = selectedEntity.id
      requestBody.name = selectedEntity.name
      requestBody.entityName = selectedEntity.name
    }

    if (
      values.reportType ===
      reportConstants.REPORT_TYPE['Session Details Reports']
    ) {
      const filters = values.sessionFilters || []
      requestBody.fields = {
        includeRegularSessions: filters.some(
          (f) => f.value === 'Regular Sessions',
        ),
        includeIntentions: filters.some(
          (f) => f.value === 'Intentions & Insights',
        ),
        includeGrowthSessions: filters.some(
          (f) => f.value === 'Growth Sessions',
        ),
        includeGroupSessions: filters.some((f) => f.value === 'Group Sessions'),
      }
    }

    return requestBody
  }

  const getSelectedEntityName = (values) => {
    const selectedEntity = entityNames.find(
      (entity) => entity.id === values.entityName,
    )
    return selectedEntity?.name || ''
  }

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

  const NO_DATA_MESSAGE =
    'No data is available for this report because no users are currently assigned to the selected entity.'

  const isNoDataError = (value) => {
    if (!value) {
      return false
    }
    if (typeof value === 'string') {
      return value.toLowerCase() === 'no_data'
    }
    const code = value.code || value.error
    const message = value.message
    return (
      (typeof code === 'string' && code.toLowerCase() === 'no_data') ||
      (typeof message === 'string' && message.toLowerCase() === 'no_data')
    )
  }

  // Returns job status when ready, or null if failed/timed out (toast already shown)
  const pollReportJob = async (jobId) => {
    const { REPORT_JOB_STATUS, POLL_INTERVAL_MS, POLL_MAX_ATTEMPTS } =
      reportConstants

    for (let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++) {
      const { data } = await API.AdminAPI.report.getExportStatus(jobId)
      const status = data?.status

      if (status === REPORT_JOB_STATUS.READY) {
        return data
      }

      if (
        status === REPORT_JOB_STATUS.ERROR ||
        status === REPORT_JOB_STATUS.FAILED
      ) {
        Notification.showNotification(
          isNoDataError(data?.error) || isNoDataError(data)
            ? NO_DATA_MESSAGE
            : data?.error || 'Report generation failed.',
          'danger',
        )
        return null
      }

      await sleep(POLL_INTERVAL_MS)
    }

    Notification.showNotification(
      'Report generation timed out. Please try again.',
      'danger',
    )
    return null
  }

  // Returns { jobId, jobStatus } or null when job cannot be completed
  const startReportJob = async (values, { isExport = false } = {}) => {
    const requestBody = buildReportRequestBody(values)

    const { data } = isExport
      ? await API.AdminAPI.report.exportReport(values.reportType, requestBody)
      : await API.AdminAPI.report.viewReport(values.reportType, requestBody)

    const jobId = data?.jobId || data?.data?.jobId

    if (!jobId) {
      Notification.showNotification(
        isNoDataError(data?.message) ||
          isNoDataError(data?.data?.message) ||
          isNoDataError(data) ||
          isNoDataError(data?.data)
          ? NO_DATA_MESSAGE
          : data?.message ||
              data?.data?.message ||
              'Report job could not be started. Please try again.',
        'danger',
      )
      return null
    }

    const jobStatus = await pollReportJob(jobId)
    if (!jobStatus) {
      return null
    }

    return { jobId, jobStatus }
  }

  const handleExportReport = async (values) => {
    if (!validateReportFilters(values) || requestInFlightRef.current) {
      return
    }

    try {
      requestInFlightRef.current = true
      setExportLoading(true)

      const result = await startReportJob(values, { isExport: true })
      if (!result) {
        return
      }

      const { jobId, jobStatus } = result
      const { data: blob } = await API.AdminAPI.report.downloadReport(jobId, {
        asBlob: true,
      })

      if (blob?.size > 0) {
        const entityType = values.entityType || 'all'
        const rawEntityName = getSelectedEntityName(values) || 'report'
        const filename = (
          jobStatus?.fileName
            ? `${jobStatus.fileName}.xlsx`
            : `${rawEntityName} (${entityType}) - ${values.reportType} Report.xlsx`
        )
          .replace(/[/\\?%*:|"<>]/g, '')
          .trim()

        const downloadLink = document.createElement('a')
        const url = window.URL.createObjectURL(blob)

        downloadLink.href = url
        downloadLink.download = filename
        downloadLink.style.display = 'none'

        document.body.appendChild(downloadLink)
        downloadLink.click()

        document.body.removeChild(downloadLink)
        window.URL.revokeObjectURL(url)

        Notification.showNotification(
          'Report exported successfully.',
          'success',
        )
      }
    } catch (error) {
      if (isNoDataError(error)) {
        Notification.showNotification(NO_DATA_MESSAGE, 'danger')
      } else if (
        error?.code === 'report_job_not_found' ||
        error?.code === 'report_not_ready' ||
        error?.code === 'report_file_missing'
      ) {
        Notification.showNotification(
          error.message || 'Unable to download report.',
          'danger',
        )
      } else {
        Error.showError(error)
      }
    } finally {
      requestInFlightRef.current = false
      setExportLoading(false)
    }
  }

  const handleViewReport = async (values) => {
    if (!validateReportFilters(values) || requestInFlightRef.current) {
      return
    }

    try {
      requestInFlightRef.current = true
      setReportLoading(true)

      const result = await startReportJob(values, { isExport: false })
      if (!result) {
        return
      }

      const { data } = await API.AdminAPI.report.downloadReport(result.jobId)
      setReportData({ ...data, reportType: values.reportType })
    } catch (error) {
      if (isNoDataError(error)) {
        Notification.showNotification(NO_DATA_MESSAGE, 'danger')
      } else if (
        error?.code === 'report_job_not_found' ||
        error?.code === 'report_not_ready' ||
        error?.code === 'report_file_missing'
      ) {
        Notification.showNotification(
          error.message || 'Unable to download report.',
          'danger',
        )
      } else {
        Error.showError(error)
      }
      setReportData(null)
    } finally {
      requestInFlightRef.current = false
      setReportLoading(false)
    }
  }

  const renderReportTable = () => {
    if (!reportData) {
      return null
    }

    if (
      reportData.reportType ===
      reportConstants.REPORT_TYPE['Session Details Reports']
    ) {
      return <SessionDetailsTable reportData={reportData} />
    }

    let data = []
    let keyValueDataForColumnName = {}

    if (reportData.data && reportData.keyValueDataForColumnName) {
      data = reportData.data
      keyValueDataForColumnName = reportData.keyValueDataForColumnName
    } else if (
      reportData.data &&
      reportData.data.data &&
      reportData.data.keyValueDataForColumnName
    ) {
      data = reportData.data.data
      keyValueDataForColumnName = reportData.data.keyValueDataForColumnName
    } else if (Array.isArray(reportData)) {
      data = reportData
      if (data.length > 0) {
        const firstItem = data[0]
        keyValueDataForColumnName = {}
        Object.keys(firstItem).forEach((key) => {
          // Column labels come from BE; use key as-is when mapping is absent
          keyValueDataForColumnName[key] = key
        })
      }
    }

    if (!data || data.length === 0) {
      return (
        <div className='text-center py-4'>
          <h3 className='font-montserrat'>No Report Data Found.</h3>
        </div>
      )
    }

    const columnKeys = Object.keys(keyValueDataForColumnName)

    return (
      <Container
        className='account-report custom-offset-lg-1 custom-col-lg-12 px-0'
        fluid>
        <h2 className='text-center'>
          {reportData?.reportType
            ? Object.keys(reportConstants.REPORT_TYPE).find(
                (key) =>
                  reportConstants.REPORT_TYPE[key] === reportData.reportType,
              )
            : 'REPORT'}
        </h2>
        <Row className='m-0 d-lg-block'>
          <div className='col-xs-12'>
            <Table hover responsive className='custom-table'>
              <thead>
                <tr>
                  <th>#</th>
                  {columnKeys.map((key) => (
                    <th key={key}>{keyValueDataForColumnName[key]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((record, index) => (
                  <tr key={record.userId || record.id || index}>
                    <td>{index + 1}</td>
                    {columnKeys.map((key) => {
                      const value = record[key]
                      const isClickable = isClickableColumn(key)

                      if (Array.isArray(value)) {
                        if (value.length === 0) {
                          return <td key={key}>-</td>
                        }
                        return (
                          <td key={key}>
                            {value.map((session, sessionIndex) => (
                              <div key={sessionIndex} className='mb-2 p-2'>
                                <div>
                                  <strong>Date:</strong> {session.date}
                                </div>
                                <div>
                                  <strong>Coach:</strong> {session.coach}
                                </div>
                              </div>
                            ))}
                          </td>
                        )
                      }

                      return (
                        <td
                          key={key}
                          style={{
                            ...(isSaffronColumn(key) && value && value !== '-'
                              ? { color: '#bf9000' }
                              : {}),
                            ...(isClickable && value && value !== '-'
                              ? { cursor: 'pointer' }
                              : {}),
                          }}
                          onClick={() => {
                            if (isClickable && value && value !== '-') {
                              const userId =
                                record.userId || record.clientId || record.id
                              handleUserClick(userId)
                            }
                          }}>
                          {value !== null && value !== undefined
                            ? String(value)
                            : '-'}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Row>
      </Container>
    )
  }

  const isNoFilterSelected = (values) => {
    return (
      !values.reportType &&
      !values.entityType &&
      !values.entityName &&
      (!Array.isArray(values.sessionFilters) ||
        values.sessionFilters.length === 0)
    )
  }

  return (
    <div>
      <PageHeader
        titleText='Reports'
        colClasses='px-0'
        titleClasses='sentence-case-text'
      />
      <section className='account pt-5'>
        <Container fluid className='custom-offset-lg-1 custom-col-lg-12 px-0'>
          <Formik initialValues={initialValues} onSubmit={() => {}}>
            {({ values, setFieldValue }) => (
              <Form
                onSubmit={(e) => {
                  e.preventDefault()
                }}>
                <Row className='custom-report-container'>
                  <Col xs={4} className='mb-2'>
                    <select
                      name='reportType'
                      className='account-daterange report-coach profile-select bg-white-shadow user-input form-control'
                      id='reportType'
                      value={values.reportType}
                      onChange={(e) => {
                        const selectedValue = String(e.target.value || '')

                        setFieldValue('reportType', selectedValue)
                        setFieldValue('entityType', '')
                        setFieldValue('entityName', '')
                        setFieldValue('sessionFilters', [])
                        setEntityNames([])
                        setReportData(null)
                      }}>
                      <option value='' disabled>
                        Report Type
                      </option>
                      {Object.keys(reportConstants.REPORT_TYPE).map((key) => (
                        <option
                          value={reportConstants.REPORT_TYPE[key]}
                          key={reportConstants.REPORT_TYPE[key]}>
                          {key}
                        </option>
                      ))}
                    </select>
                  </Col>
                  <Col xs={4} className='mb-2'>
                    <select
                      name='entityType'
                      className='account-daterange report-coach profile-select bg-white-shadow user-input form-control'
                      id='entityType'
                      value={values.entityType}
                      onChange={(e) => {
                        const selectedEntityType = String(e.target.value || '')
                        setFieldValue('entityType', selectedEntityType)
                        setFieldValue('entityName', '')
                        fetchEntityNames(selectedEntityType)
                        setReportData(null)
                      }}>
                      <option value='' disabled>
                        Entity Type
                      </option>
                      {Object.keys(reportConstants.ENTITY_TYPE).map((key) => (
                        <option
                          value={reportConstants.ENTITY_TYPE[key]}
                          key={reportConstants.ENTITY_TYPE[key]}>
                          {key}
                        </option>
                      ))}
                    </select>
                  </Col>
                  <Col xs={4} className='mb-2'>
                    <select
                      name='entityName'
                      className='account-daterange report-coach profile-select bg-white-shadow user-input form-control'
                      id='entityName'
                      value={values.entityName}
                      onFocus={(e) =>
                        handleEntityNameClick(values.entityType, e)
                      }
                      onClick={(e) =>
                        handleEntityNameClick(values.entityType, e)
                      }
                      onChange={(e) => {
                        setFieldValue('entityName', e.target.value)
                        setReportData(null)
                      }}
                      disabled={entityNamesLoading}>
                      <option value='' disabled>
                        {entityNamesLoading ? 'Loading...' : 'Entity Name'}
                      </option>
                      {entityNames.map((entity) => (
                        <option value={entity.id} key={entity.id}>
                          {entity.name}
                        </option>
                      ))}
                    </select>
                  </Col>
                </Row>

                {values.reportType ===
                  reportConstants?.REPORT_TYPE?.['Session Details Reports'] && (
                  <div className='mb-3 session-details'>
                    <CustomSelectionField
                      name='sessionFilters'
                      placeholder='Fields'
                      className='mb-0'
                      classNamePrefix='selection-input'
                      options={sessionFilterOptions}
                      isMulti
                      closeMenuOnSelect={false}
                      maxMenuHeight={140}
                      value={values.sessionFilters}
                      onChange={(selectedOptions) => {
                        setFieldValue('sessionFilters', selectedOptions || [])
                      }}
                    />
                  </div>
                )}
                {isAnyFilterActive(values) && (
                  <ClearFilters
                    wrapperClasses='text-right'
                    onClick={() => handleClearFilters(setFieldValue)}
                    disabled={isLoading}
                  />
                )}

                <div className='margin-auto text-center'>
                  <Button
                    type='button'
                    variant='gray'
                    size='md'
                    className='line-height-normal text-uppercase border-radius-3'
                    disabled={isLoading || isNoFilterSelected(values)}
                    onClick={() => handleViewReport(values)}>
                    {reportLoading ? 'Loading...' : 'View Report'}
                  </Button>
                  <Button
                    type='button'
                    variant='gray'
                    id='generate-report'
                    size='md'
                    className='line-height-normal text-uppercase border-radius-3'
                    style={{ marginLeft: '10px' }}
                    disabled={isLoading || isNoFilterSelected(values)}
                    onClick={() => handleExportReport(values)}>
                    {exportLoading ? 'Exporting...' : 'Export Report'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Container>

        {reportLoading && (
          <div className='text-center py-4'>
            <Spinner animation='border' className='text-saffron-700' />
          </div>
        )}

        {!reportLoading && reportData && (
          <div className='mt-4'>{renderReportTable()}</div>
        )}
      </section>
    </div>
  )
}

export default Reports
